import React, { useState } from 'react'
import { T } from '@transifex/react'
import {
  Card,
  Icon,
  Button,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import {
  IconCheckCircleSolid20,
  IconScheduleSolid20,
} from '@lightspeed/unified-react-components'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'
import {
  getPaymentRequestViewURL,
  formatDate,
} from '../../../../functions/invoicing/payment-request-resources'
import { cancelPaymentRequest } from '../../../../functions/invoicing/cancel-payment-request'
import { useRetailerSettingsManager } from '../../../../ng-compat/hooks'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { FEATURE_ENABLE_INVOICING_OVERPAYMENT_SUPPORT } from '../../../../constants'
import { PaymentRequestOverpaymentWarning } from './PaymentRequestOverpaymentWarning'
import { usePaymentRequestCalculations } from './usePaymentRequestCalculations'

interface Props {
  paymentRequests: PaymentRequestQueryResponse[] // List of payment requests for the current sale
  onRefetch?: () => Promise<void> // Callback to refetch payment requests
  outstandingBalance?: number // Outstanding balance of the sale in dollars for overpayment calculation
  amountTendered?: string // Amount tendered on the payment panel in dollars
}

const PaymentRequestStatus = {
  SENT: 'sent',
  PAID: 'paid',
} as const

export const PaymentRequestSummaryCard: React.FC<Props> = ({
  paymentRequests,
  onRefetch,
  outstandingBalance,
  amountTendered,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set())
  const retailerSettingsManager = useRetailerSettingsManager()
  const retailer = retailerSettingsManager.get()

  // Use custom hook for calculations
  const {
    visibleRequests,
    pendingRequests,
    overpaymentThresholdDollars,
    totalPendingAmountDollars,
  } = usePaymentRequestCalculations(paymentRequests, outstandingBalance)

  if (visibleRequests.length === 0) {
    return null
  }

  // Show overpayment warning when:
  // 1. Feature flag is enabled
  // 2. There are pending payment requests
  // 3. Outstanding balance is defined (sale has balance to pay)
  // 4. Amount tendered is defined (user has entered payment amount)
  // 5. Amount tendered exceeds the overpayment threshold (outstanding balance minus pending requests total)
  const shouldShowWarning =
    getFeatureFromCache(FEATURE_ENABLE_INVOICING_OVERPAYMENT_SUPPORT) &&
    pendingRequests.length > 0 &&
    outstandingBalance !== undefined &&
    amountTendered !== undefined &&
    parseFloat(amountTendered) > overpaymentThresholdDollars

  const getStatusIcon = (status: string) => {
    switch (status) {
      case PaymentRequestStatus.SENT:
        return (
          <IconScheduleSolid20
            style={{ marginRight: '8px' }}
            className="vd-text-info"
          />
        )
      case PaymentRequestStatus.PAID:
        return (
          <IconCheckCircleSolid20
            style={{ marginRight: '8px' }}
            className="vd-text-success"
          />
        )
      default:
        return null
    }
  }

  const getPaidDate = (request: PaymentRequestQueryResponse): string => {
    const capturedPayment = request.payments?.find(
      payment => payment.status === 'captured'
    )

    return capturedPayment ? formatDate(capturedPayment.capturedAt) : ''
  }

  const getStatusText = (request: PaymentRequestQueryResponse) => {
    // Convert from cents to dollars
    const amount = request.requestedAmount / 100

    if (request.status === PaymentRequestStatus.SENT) {
      const sentDate = formatDate(request.createdAt)
      return (
        <T
          _str="A {amount} payment request was sent on {date}."
          _context="Payment request pending status"
          amount={<Currency value={amount} />}
          date={sentDate}
        />
      )
    }

    if (request.status === PaymentRequestStatus.PAID) {
      const paidDate = getPaidDate(request)
      return (
        <T
          _str="{amount} request paid on {date}"
          _context="Payment request paid status"
          amount={<Currency value={amount} />}
          date={paidDate}
        />
      )
    }

    return null
  }

  const handleViewClick = (request: PaymentRequestQueryResponse) => {
    const viewUrl = getPaymentRequestViewURL(request.proposalHash, retailer)
    window.open(viewUrl, '_blank', 'noopener,noreferrer')
  }

  const handleCancelClick = async (
    request: PaymentRequestQueryResponse,
    event: React.MouseEvent
  ) => {
    event.stopPropagation() // Prevent expanding/collapsing when clicking cancel

    // Mark as cancelling to show loading state
    setCancellingIds(prev => new Set(prev).add(request.id))

    try {
      const result = await cancelPaymentRequest(request.id)

      if (result.success) {
        ToastNotifications.success(
          `Payment request #${request.id} has been cancelled.`
        )
        // Refetch to update the UI
        if (onRefetch) {
          await onRefetch()
        }
      } else {
        ToastNotifications.negative(
          result.error || 'Failed to cancel payment request'
        )
      }
    } catch (error) {
      ToastNotifications.negative('Failed to cancel payment request')
    } finally {
      // Remove from cancelling set
      setCancellingIds(prev => {
        const next = new Set(prev)
        next.delete(request.id)
        return next
      })
    }
  }

  const renderSingleRequest = () => {
    const request = visibleRequests[0]
    return (
      <Card
        className="wr-payment-request-summary-card"
        style={{ marginBottom: '16px' }}
      >
        <div style={{ padding: '16px 20px' }}>
          {shouldShowWarning && (
            <PaymentRequestOverpaymentWarning
              pendingCount={pendingRequests.length}
              totalPendingAmount={totalPendingAmountDollars}
              thresholdAmount={overpaymentThresholdDollars}
            />
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flexShrink: 0, paddingTop: '2px' }}>
              {getStatusIcon(request.status)}
            </div>
            <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
              <div
                style={{
                  marginBottom: '12px',
                  lineHeight: '1.5',
                  fontSize: '14px',
                }}
              >
                {getStatusText(request)}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  link
                  className="vd-link vd-link--secondary"
                  onClick={() => handleViewClick(request)}
                >
                  <T _str="View" _context="Payment request view link" />
                  <Icon icon="fa-external-link" style={{ marginLeft: '4px' }} />
                </Button>
                {request.status === PaymentRequestStatus.SENT && (
                  <Button
                    link
                    className="vd-link vd-link--secondary"
                    onClick={e => handleCancelClick(request, e)}
                    disabled={cancellingIds.has(request.id)}
                  >
                    <T _str="Cancel" _context="Payment request cancel link" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  const renderMultipleRequests = () => {
    return (
      <Card
        className="wr-payment-request-summary-card"
        style={{ marginBottom: '16px' }}
      >
        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            gap: '12px',
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              flex: 1,
            }}
          >
            <strong style={{ fontSize: '15px' }}>
              <T
                _str="Payment requests"
                _context="Payment requests section title"
              />
            </strong>
            {pendingRequests.length > 0 && (
              <span
                className="vd-text-info"
                style={{
                  fontSize: '12px',
                  padding: '2px 6px',
                  backgroundColor: '#e0e0e0',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                <T
                  _str="PENDING {amount}"
                  _context="Payment requests pending badge"
                  amount={<Currency value={totalPendingAmountDollars} />}
                />
              </span>
            )}
          </div>
          <Icon
            icon={isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}
            style={{ flexShrink: 0 }}
          />
        </div>
        {shouldShowWarning && (
          <div style={{ padding: '0 20px' }}>
            <PaymentRequestOverpaymentWarning
              pendingCount={pendingRequests.length}
              totalPendingAmount={totalPendingAmountDollars}
              thresholdAmount={overpaymentThresholdDollars}
            />
          </div>
        )}
        {isExpanded && (
          <div style={{ padding: '0 20px 20px' }}>
            {visibleRequests.map((request, index) => (
              <div
                key={request.id}
                style={{
                  paddingTop: index === 0 ? '0' : '16px',
                  paddingBottom:
                    index === visibleRequests.length - 1 ? '0' : '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flexShrink: 0, paddingTop: '2px' }}>
                    {getStatusIcon(request.status)}
                  </div>
                  <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <div
                      style={{
                        marginBottom: '12px',
                        lineHeight: '1.5',
                        fontSize: '14px',
                      }}
                    >
                      {getStatusText(request)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        link
                        className="vd-link vd-link--secondary"
                        onClick={() => handleViewClick(request)}
                      >
                        <T _str="View" _context="Payment request view link" />
                        <Icon
                          icon="fa-external-link"
                          style={{ marginLeft: '4px' }}
                        />
                      </Button>
                      {request.status === PaymentRequestStatus.SENT && (
                        <Button
                          link
                          className="vd-link vd-link--secondary"
                          onClick={e => handleCancelClick(request, e)}
                          disabled={cancellingIds.has(request.id)}
                        >
                          <T
                            _str="Cancel"
                            _context="Payment request cancel link"
                          />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    )
  }

  return visibleRequests.length === 1
    ? renderSingleRequest()
    : renderMultipleRequests()
}
